import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { HeartPulse, Shield, Flame, Sparkles as SparklesIcon, HandHelping, Moon, ScrollText, Lock } from "lucide-react";

const ICONS = {
  soins: HeartPulse,
  protection: Shield,
  exorcisme: Flame,
  aide: HandHelping,
  esoterisme: SparklesIcon,
  wicca: Moon,
};

const CATEGORY_INTROS = {
  soins: {
    title: "Prières de soins et de guérison",
    intro:
      "Les prières de soins accompagnent depuis les premiers siècles chrétiens celles et ceux qui cherchent l'apaisement du corps et de l'âme. On y trouve les prières traditionnelles adressées à Notre-Dame de Lourdes, à Saint Raphaël l'Archange, ainsi que des oraisons plus rares issues des bréviaires anciens. Ces textes ne remplacent pas un suivi médical : ils invitent au recueillement, à la confiance et à la persévérance dans l'espérance.",
    keywords: "prière de guérison, prière pour les malades, prière Saint Raphaël, prière Notre-Dame de Lourdes, prière apaisement",
  },
  protection: {
    title: "Prières de protection",
    intro:
      "La tradition catholique et orthodoxe compte parmi les plus riches en prières de protection : Saint Michel Archange, Saint Benoît, Saint Christophe, sans oublier la prière du Bouclier de la Sainte Trinité et la Cuirasse de Saint Patrick, sommet de la spiritualité celtique du Vᵉ siècle. Ces prières encerclent le priant d'une protection spirituelle contre les influences négatives, les envoûtements, la peur, les cauchemars et les attaques psychiques.",
    keywords: "prière de protection, prière Saint Michel, Bouclier de la Trinité, Cuirasse de Saint Patrick, prière contre le mal",
  },
  exorcisme: {
    title: "Prières d'exorcisme et de délivrance",
    intro:
      "Les prières d'exorcisme figurent parmi les textes les plus puissants de la tradition ecclésiale. Notre bibliothèque rassemble l'exorcisme de Léon XIII, les formules brèves de Saint Basile et de Saint Jean Chrysostome, ainsi que des prières de délivrance modernes issues des travaux du Père Gabriele Amorth, exorciste officiel du diocèse de Rome. Ces textes sont proposés dans un cadre éducatif et patrimonial : l'exorcisme majeur reste réservé aux prêtres formellement mandatés par leur évêque. Les fidèles peuvent en revanche réciter les prières de délivrance mineure comme oraisons personnelles.",
    keywords: "exorcisme, prière de délivrance, exorcisme Léon XIII, Père Amorth, exorcisme Saint Basile",
  },
  aide: {
    title: "Prières d'aide et de remerciements",
    intro:
      "Ces prières couvrent les moments importants de la vie : intercession pour un proche, actions de grâces, prières pour les défunts, prières pour trouver la paix intérieure ou surmonter une épreuve. La tradition catholique enseigne que la reconnaissance et la gratitude sont autant de portes ouvertes à la grâce.",
    keywords: "prière d'intercession, prière de remerciement, prière pour les défunts, action de grâce",
  },
  wicca: {
    title: "Prières wicca, celtiques et gaéliques",
    intro:
      "Le christianisme celtique et la tradition wicca partagent un rapport unique à la Création, aux quatre éléments et au cycle des saisons. Nous présentons ici des invocations élémentaires, des prières aux ancêtres, des bénédictions gaéliques anciennes ainsi que des textes de la Wicca traditionnelle. Ces prières sont proposées dans une perspective culturelle et spirituelle ouverte, sans jugement doctrinal.",
    keywords: "prière wicca, prière celtique, bénédiction gaélique, invocation élémentaire",
  },
  esoterisme: {
    title: "Rituels et pratiques ésotériques",
    intro:
      "Cette catégorie rassemble des rituels et pratiques issus des traditions ésotériques occidentales, souvent oubliées ou dispersées. Ils sont proposés avec le respect dû à leur ancienneté et le rappel constant que la pratique spirituelle authentique demande discernement, cadre et intention pure.",
    keywords: "rituel ésotérique, pratique spirituelle traditionnelle",
  },
};

const ORDER = ["soins", "protection", "exorcisme", "aide", "wicca", "esoterisme"];

const ToutesLesPrieres = () => {
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/prayers").then(({ data }) => {
      setPrayers(data);
      setLoading(false);
    });
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    prayers.forEach((p) => {
      const cat = p.category_slug || "aide";
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.title.localeCompare(b.title, "fr")));
    return map;
  }, [prayers]);

  const totalCount = prayers.length;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://prieres-soins-delivrance.fr/toutes-les-prieres#page",
        name: "Toutes les prières essentielles — Index complet",
        description:
          "Index complet de plus de 100 prières traditionnelles de soins, de protection, d'exorcisme, de délivrance, ainsi que des prières wicca, celtiques et gaéliques.",
        inLanguage: "fr-FR",
        url: "https://prieres-soins-delivrance.fr/toutes-les-prieres",
        isPartOf: { "@type": "WebSite", name: "Prières · Soins · Délivrance", url: "https://prieres-soins-delivrance.fr" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://prieres-soins-delivrance.fr/" },
          { "@type": "ListItem", position: 2, name: "Toutes les prières", item: "https://prieres-soins-delivrance.fr/toutes-les-prieres" },
        ],
      },
      {
        "@type": "ItemList",
        name: "Bibliothèque complète des prières",
        numberOfItems: totalCount,
        itemListElement: prayers.slice(0, 100).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://prieres-soins-delivrance.fr/priere/${p.slug || p.id}`,
          name: p.title,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Combien de prières sont disponibles sur Prières · Soins · Délivrance ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: `La bibliothèque compte actuellement ${totalCount || "plus de 100"} prières traditionnelles réparties en cinq grandes catégories : soins et guérison, protection, exorcisme et délivrance, aide et remerciements, prières wicca et celtiques.`,
            },
          },
          {
            "@type": "Question",
            name: "Les prières sont-elles gratuites ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Une sélection est accessible librement. L'accès à l'intégralité des prières se fait via un abonnement unique de 29 € donnant un accès à vie, sans renouvellement automatique. Le paiement se fait par Stripe.",
            },
          },
          {
            "@type": "Question",
            name: "D'où viennent ces prières ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Elles proviennent de traditions authentiques : Rituel Romain catholique, écrits des Pères de l'Église (Saint Basile, Saint Jean Chrysostome), Père Gabriele Amorth (exorciste du diocèse de Rome), bréviaires anciens, tradition celtique irlandaise et écossaise, ainsi que Wicca traditionnelle. Tous les textes sont traduits ou adaptés en français par MCS-Éditions.",
            },
          },
          {
            "@type": "Question",
            name: "Puis-je pratiquer un exorcisme moi-même ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "L'exorcisme majeur est réservé aux prêtres mandatés par leur évêque. En revanche, les prières de délivrance mineure et les prières de protection peuvent être récitées librement par tout fidèle comme oraisons personnelles.",
            },
          },
          {
            "@type": "Question",
            name: "Ces prières remplacent-elles un suivi médical ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Non, en aucun cas. Les prières et rituels présentés ne constituent ni un avis médical, ni un traitement, ni une alternative à un suivi professionnel. Pour toute question de santé physique ou mentale, consultez un professionnel qualifié.",
            },
          },
        ],
      },
    ],
  };

  useSEO({
    title: "Toutes les prières essentielles — Index complet",
    description: `Index complet de ${totalCount || "plus de 100"} prières traditionnelles : soins, guérison, protection, exorcisme, délivrance, wicca et prières celtiques. La porte d'entrée pour explorer toute la bibliothèque.`,
    path: "/toutes-les-prieres",
    keywords:
      "toutes les prières, index prières, liste prières catholiques, prière de guérison, prière de protection, prière d'exorcisme, prière wicca, prière celtique, bibliothèque prières traditionnelles",
    jsonLd,
  });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20" data-testid="all-prayers-page">
      <nav aria-label="Fil d'Ariane" className="mb-8 font-engraved text-[11px] text-[var(--ivory-muted)] tracking-widest">
        <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
        <span className="mx-2 text-[var(--gold)]">/</span>
        <span className="text-[var(--ivory)]">Toutes les prières</span>
      </nav>

      <header className="mb-16">
        <p className="font-engraved text-[var(--gold)] text-[11px] tracking-widest mb-4">Index complet</p>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--ivory)] leading-tight" data-testid="all-prayers-title">
          Toutes les <em className="text-[var(--gold)]">prières essentielles</em>
        </h1>
        <p className="mt-8 font-serif-body text-[var(--ivory-muted)] text-lg leading-relaxed max-w-3xl">
          Bienvenue dans l'index complet de la bibliothèque. Vous y trouverez l'intégralité des{" "}
          <strong className="text-[var(--ivory)]">{totalCount || "100+"} prières</strong> traditionnelles rassemblées par
          MCS-Éditions, classées par grande famille spirituelle. Cette page a été pensée comme la porte d'entrée
          principale du site : chaque titre est un lien direct vers le texte intégral de la prière.
        </p>
        <div className="mt-8 sacred-card sharp p-6 border-l-4 border-l-[var(--gold)] max-w-3xl">
          <p className="font-serif-body text-[var(--ivory-muted)] text-sm leading-relaxed">
            <span className="font-engraved text-[var(--gold)] text-[10px] mr-2">Rappel :</span>
            Les prières présentées sur ce site ne constituent ni un avis médical, ni une alternative à un suivi
            professionnel. Certains textes d'exorcisme sont proposés à titre patrimonial et éducatif.
          </p>
        </div>
      </header>

      {/* Long-form intro for GEO */}
      <section className="prose prose-invert max-w-3xl mb-20 space-y-6 font-serif-body text-[var(--ivory-muted)] text-base leading-relaxed">
        <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Pourquoi une bibliothèque de prières ?</h2>
        <p>
          Depuis les premiers siècles, la prière accompagne le croyant dans les moments d'épreuve comme dans ceux de
          gratitude. Pourtant, les prières traditionnelles les plus précieuses — celles issues des Pères de l'Église,
          des rituels anciens, des traditions celtiques ou de la sagesse wicca — sont aujourd'hui dispersées dans des
          ouvrages rares, souvent introuvables. Notre travail éditorial consiste à les rassembler, les traduire lorsque
          nécessaire, et les rendre accessibles dans un format clair et respectueux de leur source originelle.
        </p>
        <p>
          Chaque prière de cette bibliothèque a été sélectionnée pour son enracinement traditionnel, sa profondeur
          spirituelle et son utilité pastorale. Vous trouverez ainsi côte à côte des textes latins du Rituel Romain,
          des invocations élémentaires wicca, la Cuirasse de Saint Patrick, des prières mariales rares, ou encore les
          formules brèves de délivrance du Père Gabriele Amorth.
        </p>

        <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Comment utiliser cette page ?</h2>
        <p>
          Parcourez librement les cinq grandes catégories ci-dessous. Cliquez sur le titre de la prière qui vous
          attire : vous serez conduit vers son texte intégral, accompagné d'un extrait de présentation et parfois d'un
          PDF téléchargeable. Les prières marquées d'un cadenas <Lock className="inline text-[var(--gold)]" size={13} />{" "}
          sont réservées aux abonnés (accès à vie pour 29 €).
        </p>
      </section>

      {loading ? (
        <p className="text-[var(--ivory-muted)] italic font-serif-body text-center py-20">Chargement de la bibliothèque…</p>
      ) : (
        <div className="space-y-24">
          {ORDER.map((catSlug) => {
            const items = grouped[catSlug];
            if (!items || items.length === 0) return null;
            const meta = CATEGORY_INTROS[catSlug];
            const Icon = ICONS[catSlug] || ScrollText;
            return (
              <section key={catSlug} id={`categorie-${catSlug}`} data-testid={`section-${catSlug}`}>
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[rgba(212,175,55,0.2)]">
                  <Icon className="text-[var(--gold)]" size={32} strokeWidth={1.1} />
                  <div>
                    <p className="font-engraved text-[var(--gold)] text-[10px] tracking-widest">
                      {items.length} prière{items.length > 1 ? "s" : ""}
                    </p>
                    <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--ivory)] leading-tight">
                      {meta.title}
                    </h2>
                  </div>
                </div>
                <p className="font-serif-body italic text-[var(--ivory-muted)] leading-relaxed max-w-3xl mb-8">
                  {meta.intro}
                </p>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                  {items.map((p) => (
                    <li key={p.id} className="border-b border-[rgba(212,175,55,0.08)] pb-2" data-testid={`prayer-item-${p.id}`}>
                      <Link
                        to={`/priere/${p.slug || p.id}`}
                        className="group flex items-start gap-2 font-serif-body text-[var(--ivory)] hover:text-[var(--gold)] transition"
                      >
                        <span className="text-[var(--gold)] text-sm mt-1">†</span>
                        <span className="flex-1">
                          {p.title}
                          {p.is_premium && (
                            <Lock className="inline ml-2 text-[var(--gold)] opacity-70" size={11} strokeWidth={1.4} />
                          )}
                        </span>
                        <span className="opacity-0 group-hover:opacity-100 text-[var(--gold)] text-xs transition">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <section className="mt-24 sacred-card sharp p-10 text-center border border-[rgba(212,175,55,0.3)]" data-testid="cta-block">
        <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--ivory)] mb-4">
          Accédez à l'intégralité pour <em className="text-[var(--gold)]">29 €</em> à vie
        </h2>
        <p className="font-serif-body text-[var(--ivory-muted)] max-w-2xl mx-auto mb-8">
          Un unique paiement pour accéder pour toujours à toutes les prières scellées, sans renouvellement automatique
          et sans publicité.
        </p>
        <Link to="/abonnement" className="btn-sacred btn-sacred-filled sharp inline-block" data-testid="pillar-cta-abonnement">
          Rejoindre le sanctuaire
        </Link>
      </section>
    </div>
  );
};

export default ToutesLesPrieres;
